import { useState } from 'react';
import { ReuseSettings, SourceBanner, StartFrom } from '@/components/reuse';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, Eye, Image, Plus, Users } from 'lucide-react';
import {
  AUDIENCE_BADGES,
  type Audience,
  type AudienceScope,
  EVENT_KIND_LABELS,
  LEVELS,
  LEVEL_LABELS,
  eventInputSchema,
  zoneAbbreviation,
  type EventInput,
  type EventKind,
} from '@ready/shared';
import { AudiencePicker } from '@/components/audience-picker';
import { useApi, type ContentOwner, type EventDraft, type EventStatus } from '@/lib/api';
import { allTimeZones, browserTimeZone, formatDate } from '@/lib/dates';
import { useDebounced } from '@/lib/use-debounced';
import { SearchBox, TopicPicker } from '@/components/catalog';
import { CopyLinkButton, ListCount, PostersButton } from '@/components/quiz/authoring';
import {
  Badge,
  Button,
  Card,
  ErrorText,
  Field,
  Input,
  PageHeader,
  Select,
  Textarea,
} from '@/components/ui';
import { EventSessions } from './authoring-sessions';

export { EventAttendeesPage } from './attendees';

/*
 * Workshop and webinar authoring, shared by an org's Events tab
 * (`/organizations/:orgId/events`) and Nirvik's Library (`/admin/library/events`).
 * `base` is the route prefix for the owner's pages.
 */

const ownerKey = (o: ContentOwner) => (o.kind === 'org' ? o.orgId : 'library');
export const eventListPath = (e: { ownerOrgId: string | null }) =>
  e.ownerOrgId ? `/organizations/${e.ownerOrgId}/library/events` : '/admin/library/events';

export const EVENT_STATUS_LABELS: Record<EventStatus, string> = {
  draft: 'Draft',
  published: 'Live',
  cancelled: 'Cancelled',
  archived: 'Archived',
};

/** "Tue, Nov 4, 2026, 6:00 PM IST" in the given zone. */
export function formatEventStart(at: string, timeZone: string) {
  const d = new Date(at);
  const text = d.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  });
  return `${text} ${zoneAbbreviation(d, timeZone)}`;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

// ─── List ──────────────────────────────────────────────────────────────

export function EventList({ owner, base }: { owner: ContentOwner; base: string }) {
  const api = useApi();
  const [text, setText] = useState('');
  const q = useDebounced(text.trim());
  const events = useQuery({
    queryKey: ['events', ownerKey(owner), q],
    queryFn: () => api.events(owner, q),
    placeholderData: (prev) => prev,
  });
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Live workshops and webinars: online, in person or both, with registration, reminders and
          check-in.
        </p>
        <Link to={`${base}/events/new`} className="w-full sm:w-auto">
          <Button className="w-full sm:w-auto">
            <Plus className="size-4" aria-hidden /> New workshop or webinar
          </Button>
        </Link>
      </div>
      <SearchBox value={text} onChange={setText} placeholder="Search events by title" />
      <ErrorText error={events.error} />
      {events.data && <ListCount shown={events.data.events.length} total={events.data.total} />}
      {events.data?.events.length === 0 && (
        <Card>
          <p className="text-sm text-muted-foreground">
            {q
              ? 'No events match that title.'
              : 'No workshops or webinars yet. Create one, add its sessions, then publish it to open registration.'}
          </p>
        </Card>
      )}
      {events.data?.events.map((e) => (
        <Card key={e.id} className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <Link
              to={`/events/${e.id}/edit`}
              className="-my-2 inline-block py-2 font-medium hover:text-primary"
            >
              {e.title}
            </Link>
            <p className="mt-0.5 flex items-center gap-1.5 text-sm text-muted-foreground">
              <CalendarDays className="size-4 shrink-0" aria-hidden />
              {e.startsAt ? formatEventStart(e.startsAt, e.timeZone) : 'No sessions yet'}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Badge>{EVENT_KIND_LABELS[e.kind]}</Badge>
              <Badge>{EVENT_STATUS_LABELS[e.status]}</Badge>
              {e.sessionCount > 1 && <Badge>{plural(e.sessionCount, 'session')}</Badge>}
              <Badge>{AUDIENCE_BADGES[e.audience]}</Badge>
              {e.status !== 'draft' && (
                <Badge>
                  {e.registered} registered
                  {e.waitlisted > 0 ? ` · ${e.waitlisted} waitlisted` : ''}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {e.status === 'published' && (
              <>
                <CopyLinkButton path={`/e/${e.slug}`} />
                <PostersButton path={`/e/${e.slug}`} />
              </>
            )}
            <Link to={`/events/${e.id}/attendees`}>
              <Button variant="ghost">Attendees</Button>
            </Link>
            <Link to={`/events/${e.id}/edit`}>
              <Button variant="ghost">Edit</Button>
            </Link>
          </div>
        </Card>
      ))}
    </div>
  );
}

// ─── New event ─────────────────────────────────────────────────────────

const KIND_CHOICES: { kind: EventKind; hint: string }[] = [
  { kind: 'workshop', hint: 'One or more sessions, hands-on' },
  { kind: 'webinar', hint: 'One session: a live talk or panel' },
];

function TimeZoneSelect({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (tz: string) => void;
}) {
  const zones = allTimeZones();
  if (!zones.length)
    return <Input id={id} required value={value} onChange={(e) => onChange(e.target.value)} />;
  const list = zones.includes(value) ? zones : [value, ...zones];
  return (
    <Select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
      {list.map((z) => (
        <option key={z} value={z}>
          {z.replaceAll('_', ' ')}
        </option>
      ))}
    </Select>
  );
}

export function NewEventForm({
  owner,
  back,
}: {
  owner: ContentOwner;
  back: { to: string; label: string };
}) {
  const api = useApi();
  const navigate = useNavigate();
  const [kind, setKind] = useState<EventKind>('workshop');
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [timeZone, setTimeZone] = useState(browserTimeZone);
  const [audience, setAudience] = useState<Audience>(owner.kind === 'library' ? 'public' : 'org');
  const [scope, setScope] = useState<AudienceScope | null>(null);
  const create = useMutation({
    mutationFn: () =>
      api.createEvent(owner, {
        kind,
        title: title.trim(),
        summary: summary.trim() || undefined,
        timeZone,
        audience,
        audienceScope: scope,
      }),
    onSuccess: (event) => navigate(`/events/${event.id}/edit`),
  });
  return (
    <div className="max-w-3xl">
      <PageHeader
        title="New workshop or webinar"
        description="Name it and choose its time zone. You'll add sessions next."
        back={back}
      />
      <Card>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <fieldset className="space-y-1.5">
            <legend className="text-sm font-medium">Type</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {KIND_CHOICES.map((c) => (
                <label
                  key={c.kind}
                  className={`flex cursor-pointer gap-3 rounded-lg border p-3 ${kind === c.kind ? 'border-primary bg-primary/5' : 'border-border'}`}
                >
                  <input
                    type="radio"
                    name="event-kind"
                    className="mt-0.5 size-4 accent-primary"
                    checked={kind === c.kind}
                    onChange={() => setKind(c.kind)}
                  />
                  <span>
                    <span className="block text-sm font-medium">{EVENT_KIND_LABELS[c.kind]}</span>
                    <span className="block text-xs text-muted-foreground">{c.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
          <Field label="Title" htmlFor="event-title">
            <Input
              id="event-title"
              required
              minLength={3}
              maxLength={200}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={
                kind === 'workshop'
                  ? 'Hands-on phishing defense for campus teams'
                  : 'Deepfake scams: what to watch for'
              }
            />
          </Field>
          <Field label="One-line summary (optional)" htmlFor="event-summary">
            <Input
              id="event-summary"
              maxLength={500}
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
            />
          </Field>
          <Field
            label="Time zone"
            htmlFor="event-tz"
            hint="You'll enter session times in this zone. Learners also see them in their own."
          >
            <TimeZoneSelect id="event-tz" value={timeZone} onChange={setTimeZone} />
          </Field>
          <AudiencePicker
            owner={owner}
            id="event-audience"
            label="Who can register"
            audience={audience}
            scope={scope}
            onChange={(a, s) => {
              setAudience(a as Audience);
              setScope(s);
            }}
          />
          <Button type="submit" className="w-full sm:w-auto" disabled={create.isPending}>
            Create and add sessions
          </Button>
          <ErrorText error={create.error} />
        </form>
      </Card>
      <div className="mt-6">
        <StartFrom kind="event" owner={owner} />
      </div>
    </div>
  );
}

// ─── Editor ────────────────────────────────────────────────────────────

const inputOf = (e: EventDraft): EventInput => ({
  kind: e.kind,
  title: e.title,
  summary: e.summary ?? undefined,
  description: e.description ?? undefined,
  hosts: e.hosts ?? undefined,
  audience: e.audience,
  audienceScope: e.audienceScope ?? null,
  reuse: e.reuse ?? null,
  timeZone: e.timeZone,
  level: e.level,
  topics: e.topics,
  tags: e.tags,
  coverAssetId: e.coverAssetId,
  settings: e.settings,
});

/** The form, tidied for the API: blanks dropped, a minimum that fits the sessions. */
function cleanInput(form: EventInput, sessions: number): EventInput {
  const blank = (s?: string) => (s?.trim() ? s : undefined);
  const { minSessions } = form.settings;
  return {
    ...form,
    summary: blank(form.summary),
    description: blank(form.description),
    hosts: blank(form.hosts),
    settings: {
      ...form.settings,
      minSessions:
        sessions >= 2 && minSessions !== null && minSessions < sessions ? minSessions : null,
    },
  };
}

function validationMessage(form: EventInput): string | null {
  const parsed = eventInputSchema.safeParse(form);
  if (parsed.success) return null;
  const labels: Record<string, string> = {
    title: 'Title',
    summary: 'Summary',
    description: 'Description',
    hosts: "Who's presenting",
    timeZone: 'Time zone',
    tags: 'Search tags',
    topics: 'Topics',
    settings: 'Seat limit',
  };
  return parsed.error.issues
    .map((i) => `${labels[String(i.path[0])] ?? String(i.path[0])}: ${i.message}`)
    .join('. ');
}

export function EventEditorPage() {
  const { eventId = '' } = useParams();
  const api = useApi();
  const event = useQuery({ queryKey: ['event', eventId], queryFn: () => api.event(eventId) });
  if (event.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (event.isError) return <ErrorText error={event.error} />;
  return <EventEditor key={event.data.id} event={event.data} />;
}

function EventEditor({ event }: { event: EventDraft }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'sessions';
  const [form, setForm] = useState<EventInput>(() => inputOf(event));
  const [saved, setSaved] = useState(() => JSON.stringify(inputOf(event)));
  const [invalid, setInvalid] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const dirty = JSON.stringify(form) !== saved;
  const sessions = event.sessions.length;

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['event', event.id] });
    void queryClient.invalidateQueries({ queryKey: ['events'] });
  };
  const saveForm = async () => {
    const input = cleanInput(form, sessions);
    const message = validationMessage(input);
    setInvalid(message);
    if (message) throw new Error(message);
    await api.updateEvent(event.id, input);
    setForm(input);
    setSaved(JSON.stringify(input));
  };
  const save = useMutation({ mutationFn: saveForm, onSuccess: refresh });
  const publish = useMutation({
    mutationFn: async () => {
      if (dirty) await saveForm();
      return api.publishEvent(event.id);
    },
    onSuccess: refresh,
  });
  const archive = useMutation({
    mutationFn: (archived: boolean) => api.archiveEvent(event.id, archived),
    onSuccess: refresh,
  });

  const status = event.status;
  const statusText: Record<EventStatus, string> = {
    draft:
      sessions === 0
        ? 'Draft: only authors can see it. Add a session, then publish to open registration.'
        : 'Draft: only authors can see it. Publish to open registration.',
    published: `Live: people can register. ${event.registered} registered${event.waitlisted ? `, ${event.waitlisted} on the waitlist` : ''}.`,
    cancelled: `Cancelled${event.cancelledAt ? ` on ${formatDate(event.cancelledAt)}` : ''}. Everyone registered was emailed.`,
    archived: 'Archived: hidden from learners. Restore it to show it again.',
  };
  const tabs = [
    { key: 'sessions', label: event.kind === 'webinar' ? 'Session' : `Sessions (${sessions})` },
    { key: 'details', label: dirty ? 'Details •' : 'Details' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={eventListPath(event)}
          className="-my-2 inline-block py-2 text-sm text-muted-foreground hover:text-foreground"
        >
          ← Workshops and webinars
        </Link>
        <div className="mt-1 space-y-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold">{event.title}</h1>
            <div className="mt-2 flex flex-wrap gap-2">
              <Badge>{EVENT_KIND_LABELS[event.kind]}</Badge>
              <Badge>{EVENT_STATUS_LABELS[status]}</Badge>
              <Badge>{AUDIENCE_BADGES[event.audience]}</Badge>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">{statusText[status]}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {status === 'draft' && (
              <Button
                className="w-full sm:w-auto"
                disabled={publish.isPending || sessions === 0}
                title={sessions === 0 ? 'Add a session first' : undefined}
                onClick={() => publish.mutate()}
              >
                {publish.isPending ? 'Publishing…' : 'Publish'}
              </Button>
            )}
            {status !== 'cancelled' && (dirty || save.isPending) && (
              <Button
                variant={status === 'draft' ? 'secondary' : 'primary'}
                className="w-full sm:w-auto"
                disabled={save.isPending}
                onClick={() => save.mutate()}
              >
                {save.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            )}
            {status === 'published' && (
              <CopyLinkButton path={`/e/${event.slug}`} label="Copy share link" />
            )}
            {(status === 'published' || status === 'cancelled') && (
              <a href={`/e/${event.slug}`} target="_blank" rel="noreferrer">
                <Button variant="secondary">
                  <Eye className="size-4" aria-hidden /> Preview
                </Button>
              </a>
            )}
            {status === 'published' && (
              <Link to={`/e/${event.slug}/poster`}>
                <Button variant="secondary">
                  <Image className="size-4" aria-hidden /> Posters
                </Button>
              </Link>
            )}
            <Link to={`/events/${event.id}/attendees`}>
              <Button variant="secondary">
                <Users className="size-4" aria-hidden /> Attendees
              </Button>
            </Link>
            {status === 'published' && !cancelling && (
              <Button variant="ghost" onClick={() => setCancelling(true)}>
                Cancel event
              </Button>
            )}
            {status === 'archived' ? (
              <Button variant="ghost" onClick={() => archive.mutate(false)}>
                Restore
              </Button>
            ) : (
              status !== 'draft' && (
                <Button
                  variant="ghost"
                  onClick={() =>
                    window.confirm('Archive this event? Learners will no longer see it.') &&
                    archive.mutate(true)
                  }
                >
                  Archive
                </Button>
              )
            )}
          </div>
        </div>
        <div className="mt-2">
          <ErrorText error={publish.error ?? archive.error} />
        </div>
      </div>

      {cancelling && <CancelEventPanel event={event} onClose={() => setCancelling(false)} />}
      <SourceBanner kind="event" source={event.source} />

      <nav className="-mx-4 overflow-x-auto px-4 [scrollbar-width:none] sm:mx-0 sm:px-0 [&::-webkit-scrollbar]:hidden">
        <div className="flex w-max min-w-full gap-1 border-b border-border">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setParams({ tab: t.key }, { replace: true })}
              aria-current={tab === t.key ? 'page' : undefined}
              className={`-mb-px whitespace-nowrap border-b-2 px-3 py-2.5 text-sm ${tab === t.key ? 'border-primary font-medium text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </nav>

      {tab === 'details' ? (
        <EventDetails
          event={event}
          form={form}
          setForm={setForm}
          onSave={() => save.mutate()}
          saving={save.isPending}
          dirty={dirty}
          error={invalid ?? save.error}
        />
      ) : (
        <EventSessions event={event} />
      )}
    </div>
  );
}

function CancelEventPanel({ event, onClose }: { event: EventDraft; onClose: () => void }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const cancel = useMutation({
    mutationFn: () => api.cancelEvent(event.id, note.trim() || undefined),
    onSuccess: () => {
      onClose();
      void queryClient.invalidateQueries({ queryKey: ['event', event.id] });
      void queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
  const people = event.registered + event.waitlisted;
  return (
    <Card title="Cancel this event?" className="border-destructive/50">
      <div className="max-w-3xl space-y-4">
        <p className="text-sm">
          Registration closes and the event page shows it's cancelled.{' '}
          {people > 0
            ? `${plural(people, 'person')} registered or waiting will be emailed, with a calendar cancellation.`
            : 'No one has registered yet.'}{' '}
          This can't be undone.
        </p>
        <Field
          label="Note to attendees (optional)"
          htmlFor="cancel-note"
          hint="Included in the email, e.g. why, or where to find a new date."
        >
          <Textarea
            id="cancel-note"
            rows={3}
            maxLength={1000}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Field>
        <div className="flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            className="inline-flex w-full items-center justify-center rounded-[var(--radius)] bg-destructive px-4 py-2 text-sm font-medium text-white hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 sm:w-auto"
            disabled={cancel.isPending}
            onClick={() => cancel.mutate()}
          >
            {cancel.isPending ? 'Cancelling…' : 'Cancel event'}
          </button>
          <Button variant="ghost" className="w-full sm:w-auto" onClick={onClose}>
            Keep it
          </Button>
        </div>
        <ErrorText error={cancel.error} />
      </div>
    </Card>
  );
}

function EventDetails({
  event,
  form,
  setForm,
  onSave,
  saving,
  dirty,
  error,
}: {
  event: EventDraft;
  form: EventInput;
  setForm: (f: (prev: EventInput) => EventInput) => void;
  onSave: () => void;
  saving: boolean;
  dirty: boolean;
  error: unknown;
}) {
  const set = (patch: Partial<EventInput>) => setForm((f) => ({ ...f, ...patch }));
  const setSettings = (patch: Partial<EventInput['settings']>) =>
    setForm((f) => ({ ...f, settings: { ...f.settings, ...patch } }));
  const sessions = event.sessions.length;
  const locked = event.status === 'cancelled';
  const owner: ContentOwner = event.ownerOrgId
    ? { kind: 'org', orgId: event.ownerOrgId }
    : { kind: 'library' };
  const [tags, setTags] = useState(form.tags.join(', '));

  return (
    <form
      className="space-y-6"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        onSave();
      }}
    >
      <fieldset disabled={locked} className="space-y-6">
        <Card title="About the event">
          <div className="max-w-3xl space-y-4">
            <Field label="Title" htmlFor="e-title">
              <Input
                id="e-title"
                required
                minLength={3}
                maxLength={200}
                value={form.title}
                onChange={(e) => set({ title: e.target.value })}
              />
            </Field>
            <Field label="One-line summary" htmlFor="e-summary">
              <Input
                id="e-summary"
                maxLength={500}
                value={form.summary ?? ''}
                onChange={(e) => set({ summary: e.target.value })}
              />
            </Field>
            <Field
              label="Description (Markdown)"
              htmlFor="e-desc"
              hint="What attendees will do or learn, who it's for, and anything to bring or install."
            >
              <Textarea
                id="e-desc"
                rows={8}
                className="font-mono text-sm"
                value={form.description ?? ''}
                onChange={(e) => set({ description: e.target.value })}
              />
            </Field>
            <Field
              label="Who's presenting"
              htmlFor="e-hosts"
              hint="For example: Dr. A. Rao, IIT Hyderabad"
            >
              <Input
                id="e-hosts"
                maxLength={300}
                value={form.hosts ?? ''}
                onChange={(e) => set({ hosts: e.target.value })}
              />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Type"
                htmlFor="e-kind"
                hint={
                  event.status !== 'draft'
                    ? "Can't change after publishing."
                    : sessions > 1
                      ? 'A webinar has one session; delete the others first.'
                      : undefined
                }
              >
                <Select
                  id="e-kind"
                  value={form.kind}
                  disabled={event.status !== 'draft'}
                  onChange={(e) => set({ kind: e.target.value as EventKind })}
                >
                  <option value="workshop">Workshop: one or more sessions</option>
                  <option value="webinar" disabled={sessions > 1}>
                    Webinar: one session
                  </option>
                </Select>
              </Field>
              <Field label="Level" htmlFor="e-level">
                <Select
                  id="e-level"
                  value={form.level}
                  onChange={(e) => set({ level: e.target.value as EventInput['level'] })}
                >
                  {LEVELS.map((l) => (
                    <option key={l} value={l}>
                      {LEVEL_LABELS[l]}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            <Field
              label="Time zone"
              htmlFor="e-tz"
              hint="Session times are entered and shown in this zone. Changing it keeps each session at the same moment, shown in the new zone."
            >
              <TimeZoneSelect
                id="e-tz"
                value={form.timeZone}
                onChange={(timeZone) => set({ timeZone })}
              />
            </Field>
            <TopicPicker value={form.topics} onChange={(topics) => set({ topics })} />
            <Field label="Search tags" htmlFor="e-tags" hint="Comma-separated.">
              <Input
                id="e-tags"
                value={tags}
                onChange={(e) => {
                  setTags(e.target.value);
                  set({
                    tags: e.target.value
                      .split(',')
                      .map((s) => s.trim())
                      .filter(Boolean)
                      .slice(0, 20),
                  });
                }}
              />
            </Field>
          </div>
        </Card>
        <Card title="Registration and certificate">
          <div className="max-w-3xl space-y-4">
            <AudiencePicker
              owner={owner}
              id="e-audience"
              label="Who can register"
              audience={form.audience}
              scope={form.audienceScope ?? null}
              onChange={(a, s) => set({ audience: a as Audience, audienceScope: s })}
            />
            <ReuseSettings
              kind="event"
              owner={owner}
              value={form.reuse}
              onChange={(reuse) => set({ reuse })}
            />
            <Field
              label="Seat limit"
              htmlFor="e-capacity"
              hint="Leave empty for no limit. Once it's full, people join a waitlist and move up when someone cancels."
            >
              <Input
                id="e-capacity"
                type="number"
                inputMode="numeric"
                min={1}
                className="sm:max-w-48"
                value={form.settings.capacity ?? ''}
                placeholder="No limit"
                onChange={(e) =>
                  setSettings({ capacity: e.target.value ? Number(e.target.value) : null })
                }
              />
            </Field>
            <label className="flex min-h-10 items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="size-5 accent-primary"
                checked={form.settings.certificate}
                onChange={(e) => setSettings({ certificate: e.target.checked })}
              />
              Award a certificate of participation to people who attend
            </label>
            {form.settings.certificate && sessions >= 2 && (
              <Field label="Sessions to attend for a certificate" htmlFor="e-min">
                <Select
                  id="e-min"
                  className="sm:max-w-64"
                  value={
                    form.settings.minSessions === null || form.settings.minSessions >= sessions
                      ? 'all'
                      : String(form.settings.minSessions)
                  }
                  onChange={(e) =>
                    setSettings({
                      minSessions: e.target.value === 'all' ? null : Number(e.target.value),
                    })
                  }
                >
                  <option value="all">All {sessions} sessions</option>
                  {Array.from({ length: sessions - 1 }, (_, i) => sessions - 1 - i).map((n) => (
                    <option key={n} value={n}>
                      At least {n} of {sessions}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
            {form.settings.certificate && (
              <p className="text-xs text-muted-foreground">
                Attendance comes from check-in codes, and you can mark it on the Attendees page.
                Learners need their name on file to register.
              </p>
            )}
          </div>
        </Card>
      </fieldset>
      {!locked && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Button type="submit" className="w-full sm:w-auto" disabled={saving || !dirty}>
            {saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
          </Button>
          {event.status === 'published' && dirty && (
            <p className="text-xs text-muted-foreground">
              Changes show on the event page right away.
            </p>
          )}
        </div>
      )}
      <ErrorText error={error} />
    </form>
  );
}
